import { EventBus } from "@saas/components/src/utils/event-bus";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
export default {
  data() {
    return {
      // 存储 liveService 监听器：{ eventName: [{ key, handler }, ...] }
      _liveServiceHandlers: {},
      // 存储 imService 监听器：{ eventName: [{ key, handler }, ...] }
      _imServiceHandlers: {},
      // 存储 EventBus 监听器：{ eventName: [{ key, handler }, ...] }
      _eventBusHandlers: {},
      // 存储 whiteBoardService 监听器：{ eventName: [{ key, handler }, ...] }
      _whiteBoardServiceHandlers: {},
      // 存储 ipcRenderer 监听器：{ eventName: [{ key, handler }, ...] }
      _ipcRendererHandlers: {},
      // 存储定时器：Map<key, { type: 'timeout' | 'interval', id: number }>
      _timers: new Map(),
      // 定时器 key 计数器（用于自动生成唯一 key）
      _timerKeyCounter: 0,
      // 监听器 key 计数器（用于自动生成唯一 key）
      _listenerKeyCounter: 0,
    };
  },
  computed: {
    //  mapState 必须在 computed 中
    ...mapState("live", ["liveService", "imService", "whiteBoardService"]),
  },
  beforeDestroy() {
    // 清理所有 liveService 监听器
    this.clearLiveServiceListeners();
    // 清理所有 imService 监听器
    this.clearImServiceListeners();
    // 清理所有 EventBus 监听器
    this.clearEventBusListeners();
    // 清理所有 whiteBoardService 监听器
    this.clearWhiteBoardServiceListeners();
    // 清理所有 ipcRenderer 监听器
    this.clearIpcRendererListeners();
    // 清理所有定时器
    this.clearAllTimers();
  },

  methods: {
    /**
     * 注册 liveService 监听器
     * @param {string} eventName - 事件名称
     * @param {Function} handler - 事件处理函数
     * @param {boolean} forceReplace - 如果已存在相同 key 的监听器，是否强制替换（默认 false）
     * @param {string} uniqueKey - 唯一标识符（可选），用于管理多个相同事件的监听器。如果不提供，会自动生成
     * @returns {string} 返回监听器的 key
     */
    registerLiveServiceListener(
      eventName,
      handler,
      forceReplace = false,
      uniqueKey = null
    ) {
      // 确保数据已初始化（防止 mixin 合并问题）
      if (!this._liveServiceHandlers) {
        this._liveServiceHandlers = {};
      }

      if (!this.liveService) {
        console.warn(
          `[eventListenerManager] liveService 未准备好，无法注册监听器: ${eventName}`
        );
        return null;
      }

      if (!eventName || typeof handler !== "function") {
        console.warn(
          `[eventListenerManager] 参数无效: eventName=${eventName}, handler=${typeof handler}`
        );
        return null;
      }

      // 初始化数组
      if (!this._liveServiceHandlers[eventName]) {
        this._liveServiceHandlers[eventName] = [];
      }

      // 生成 key（如果没有提供）
      if (!uniqueKey) {
        // 确保计数器已初始化
        if (this._listenerKeyCounter === undefined) {
          this._listenerKeyCounter = 0;
        }
        // 使用计数器 + 时间戳 + 随机数确保唯一性
        uniqueKey = `_auto_${Date.now()}_${++this
          ._listenerKeyCounter}_${Math.random().toString(36).substr(2, 9)}`;
      }

      // 检查是否已存在相同 key 的监听器
      const existingIndex = this._liveServiceHandlers[eventName].findIndex(
        (item) => item.key === uniqueKey
      );

      if (existingIndex !== -1) {
        if (!forceReplace) {
          console.warn(
            `[eventListenerManager] liveService 监听器 ${eventName} 的 key ${uniqueKey} 已存在，跳过注册（如需替换请设置 forceReplace=true）`
          );
          return uniqueKey;
        }
        // 强制替换：清理旧的
        const existingItem =
          this._liveServiceHandlers[eventName][existingIndex];
        this.liveService.off(eventName, existingItem.handler);
        this._liveServiceHandlers[eventName].splice(existingIndex, 1);
      }

      // 注册新的监听器
      this.liveService.on(eventName, handler);

      // 保存引用
      this._liveServiceHandlers[eventName].push({
        key: uniqueKey,
        handler: handler,
      });

      return uniqueKey;
    },

    /**
     * 注册 imService 监听器
     * @param {string} eventName - 事件名称
     * @param {Function} handler - 事件处理函数
     * @param {boolean} forceReplace - 如果已存在相同 key 的监听器，是否强制替换（默认 false）
     * @param {string} uniqueKey - 唯一标识符（可选），用于管理多个相同事件的监听器。如果不提供，会自动生成
     * @returns {string} 返回监听器的 key
     */
    registerImServiceListener(
      eventName,
      handler,
      forceReplace = false,
      uniqueKey = null
    ) {
      // ✅ 确保数据已初始化
      if (!this._imServiceHandlers) {
        this._imServiceHandlers = {};
      }

      if (!this.imService) {
        console.warn(
          `[eventListenerManager] imService 未准备好，无法注册监听器: ${eventName}`
        );
        return null;
      }

      if (!eventName || typeof handler !== "function") {
        console.warn(
          `[eventListenerManager] 参数无效: eventName=${eventName}, handler=${typeof handler}`
        );
        return null;
      }

      // 初始化数组
      if (!this._imServiceHandlers[eventName]) {
        this._imServiceHandlers[eventName] = [];
      }

      // 生成 key（如果没有提供）
      if (!uniqueKey) {
        // 确保计数器已初始化
        if (this._listenerKeyCounter === undefined) {
          this._listenerKeyCounter = 0;
        }
        // 使用计数器 + 时间戳 + 随机数确保唯一性
        uniqueKey = `_auto_${Date.now()}_${++this
          ._listenerKeyCounter}_${Math.random().toString(36).substr(2, 9)}`;
      }

      // 检查是否已存在相同 key 的监听器
      const existingIndex = this._imServiceHandlers[eventName].findIndex(
        (item) => item.key === uniqueKey
      );

      if (existingIndex !== -1) {
        if (!forceReplace) {
          console.warn(
            `[eventListenerManager] imService 监听器 ${eventName} 的 key ${uniqueKey} 已存在，跳过注册（如需替换请设置 forceReplace=true）`
          );
          return uniqueKey;
        }
        // 强制替换：清理旧的
        const existingItem = this._imServiceHandlers[eventName][existingIndex];
        this.imService.off(eventName, existingItem.handler);
        this._imServiceHandlers[eventName].splice(existingIndex, 1);
      }

      // 注册新的监听器
      this.imService.on(eventName, handler);

      // 保存引用
      this._imServiceHandlers[eventName].push({
        key: uniqueKey,
        handler: handler,
      });

      return uniqueKey;
    },

    /**
     * 注册 EventBus 监听器
     * @param {string} eventName - 事件名称
     * @param {Function} handler - 事件处理函数
     * @param {boolean} forceReplace - 如果已存在相同 key 的监听器，是否强制替换（默认 false）
     * @param {string} uniqueKey - 唯一标识符（可选），用于管理多个相同事件的监听器。如果不提供，会自动生成
     * @returns {string} 返回监听器的 key
     */
    registerEventBusListener(
      eventName,
      handler,
      forceReplace = false,
      uniqueKey = null
    ) {
      // ✅ 确保数据已初始化
      if (!this._eventBusHandlers) {
        this._eventBusHandlers = {};
      }

      if (!eventName || typeof handler !== "function") {
        console.warn(
          `[eventListenerManager] 参数无效: eventName=${eventName}, handler=${typeof handler}`
        );
        return null;
      }

      // 初始化数组
      if (!this._eventBusHandlers[eventName]) {
        this._eventBusHandlers[eventName] = [];
      }

      // 生成 key（如果没有提供）
      if (!uniqueKey) {
        // 确保计数器已初始化
        if (this._listenerKeyCounter === undefined) {
          this._listenerKeyCounter = 0;
        }
        // 使用计数器 + 时间戳 + 随机数确保唯一性
        uniqueKey = `_auto_${Date.now()}_${++this
          ._listenerKeyCounter}_${Math.random().toString(36).substr(2, 9)}`;
      }

      // 检查是否已存在相同 key 的监听器
      const existingIndex = this._eventBusHandlers[eventName].findIndex(
        (item) => item.key === uniqueKey
      );

      if (existingIndex !== -1) {
        if (!forceReplace) {
          console.warn(
            `[eventListenerManager] EventBus 监听器 ${eventName} 的 key ${uniqueKey} 已存在，跳过注册（如需替换请设置 forceReplace=true）`
          );
          return uniqueKey;
        }
        // 强制替换：清理旧的
        const existingItem = this._eventBusHandlers[eventName][existingIndex];
        EventBus.$off(eventName, existingItem.handler);
        this._eventBusHandlers[eventName].splice(existingIndex, 1);
      }

      // 注册新的监听器
      EventBus.$on(eventName, handler);

      // 保存引用
      this._eventBusHandlers[eventName].push({
        key: uniqueKey,
        handler: handler,
      });

      return uniqueKey;
    },

    /**
     * 注册 whiteBoardService 监听器
     * @param {string} eventName - 事件名称
     * @param {Function} handler - 事件处理函数
     * @param {boolean} forceReplace - 如果已存在相同 key 的监听器，是否强制替换（默认 false）
     * @param {string} uniqueKey - 唯一标识符（可选），用于管理多个相同事件的监听器。如果不提供，会自动生成
     * @returns {string} 返回监听器的 key
     */
    registerWhiteBoardServiceListener(
      eventName,
      handler,
      forceReplace = false,
      uniqueKey = null
    ) {
      // 确保数据已初始化
      if (!this._whiteBoardServiceHandlers) {
        this._whiteBoardServiceHandlers = {};
      }

      if (!this.whiteBoardService) {
        console.warn(
          `[eventListenerManager] whiteBoardService 未准备好，无法注册监听器: ${eventName}`
        );
        return null;
      }

      if (!eventName || typeof handler !== "function") {
        console.warn(
          `[eventListenerManager] 参数无效: eventName=${eventName}, handler=${typeof handler}`
        );
        return null;
      }

      // 初始化数组
      if (!this._whiteBoardServiceHandlers[eventName]) {
        this._whiteBoardServiceHandlers[eventName] = [];
      }

      // 生成 key（如果没有提供）
      if (!uniqueKey) {
        // 确保计数器已初始化
        if (this._listenerKeyCounter === undefined) {
          this._listenerKeyCounter = 0;
        }
        // 使用计数器 + 时间戳 + 随机数确保唯一性
        uniqueKey = `_auto_${Date.now()}_${++this
          ._listenerKeyCounter}_${Math.random().toString(36).substr(2, 9)}`;
      }

      // 检查是否已存在相同 key 的监听器
      const existingIndex = this._whiteBoardServiceHandlers[
        eventName
      ].findIndex((item) => item.key === uniqueKey);

      if (existingIndex !== -1) {
        if (!forceReplace) {
          console.warn(
            `[eventListenerManager] whiteBoardService 监听器 ${eventName} 的 key ${uniqueKey} 已存在，跳过注册（如需替换请设置 forceReplace=true）`
          );
          return uniqueKey;
        }
        // 强制替换：清理旧的
        const existingItem =
          this._whiteBoardServiceHandlers[eventName][existingIndex];
        this.whiteBoardService.off(eventName, existingItem.handler);
        this._whiteBoardServiceHandlers[eventName].splice(existingIndex, 1);
      }

      // 注册新的监听器
      this.whiteBoardService.on(eventName, handler);

      // 保存引用
      this._whiteBoardServiceHandlers[eventName].push({
        key: uniqueKey,
        handler: handler,
      });

      return uniqueKey;
    },

    /**
     * 注册 ipcRenderer 监听器（仅 Electron 环境）
     * @param {string} eventName - 事件名称
     * @param {Function} handler - 事件处理函数，格式为 (event, ...args) => {}
     * @param {boolean} forceReplace - 如果已存在相同 key 的监听器，是否强制替换（默认 false）
     * @param {string} uniqueKey - 唯一标识符（可选），用于管理多个相同事件的监听器。如果不提供，会自动生成
     * @returns {string} 返回监听器的 key
     */
    registerIpcRendererListener(
      eventName,
      handler,
      forceReplace = false,
      uniqueKey = null
    ) {
      // 确保数据已初始化
      if (!this._ipcRendererHandlers) {
        this._ipcRendererHandlers = {};
      }

      if (!window.ipcRenderer) {
        console.warn(
          `[eventListenerManager] ipcRenderer 不可用（非 Electron 环境），无法注册监听器: ${eventName}`
        );
        return null;
      }

      if (!eventName || typeof handler !== "function") {
        console.warn(
          `[eventListenerManager] 参数无效: eventName=${eventName}, handler=${typeof handler}`
        );
        return null;
      }

      // 初始化数组
      if (!this._ipcRendererHandlers[eventName]) {
        this._ipcRendererHandlers[eventName] = [];
      }

      // 生成 key（如果没有提供）
      if (!uniqueKey) {
        // 确保计数器已初始化
        if (this._listenerKeyCounter === undefined) {
          this._listenerKeyCounter = 0;
        }
        // 使用计数器 + 时间戳 + 随机数确保唯一性
        uniqueKey = `_auto_${Date.now()}_${++this
          ._listenerKeyCounter}_${Math.random().toString(36).substr(2, 9)}`;
      }

      // 检查是否已存在相同 key 的监听器
      const existingIndex = this._ipcRendererHandlers[eventName].findIndex(
        (item) => item.key === uniqueKey
      );

      if (existingIndex !== -1) {
        if (!forceReplace) {
          console.warn(
            `[eventListenerManager] ipcRenderer 监听器 ${eventName} 的 key ${uniqueKey} 已存在，跳过注册（如需替换请设置 forceReplace=true）`
          );
          return uniqueKey;
        }
        // 强制替换：清理旧的
        const existingItem =
          this._ipcRendererHandlers[eventName][existingIndex];
        window.ipcRenderer.removeListener(eventName, existingItem.handler);
        this._ipcRendererHandlers[eventName].splice(existingIndex, 1);
      }

      // 注册新的监听器
      window.ipcRenderer.on(eventName, handler);

      // 保存引用
      this._ipcRendererHandlers[eventName].push({
        key: uniqueKey,
        handler: handler,
      });

      return uniqueKey;
    },

    /**
     * 批量注册 liveService 监听器
     * @param {Object} handlers - { eventName: handler, ... }
     */
    registerLiveServiceListeners(handlers) {
      if (!handlers || typeof handlers !== "object") {
        console.warn(
          `[eventListenerManager] registerLiveServiceListeners 参数必须是对象`
        );
        return;
      }

      Object.keys(handlers).forEach((eventName) => {
        this.registerLiveServiceListener(eventName, handlers[eventName]);
      });
    },

    /**
     * 批量注册 imService 监听器
     * @param {Object} handlers - { eventName: handler, ... }
     */
    registerImServiceListeners(handlers) {
      if (!handlers || typeof handlers !== "object") {
        console.warn(
          `[eventListenerManager] registerImServiceListeners 参数必须是对象`
        );
        return;
      }

      Object.keys(handlers).forEach((eventName) => {
        this.registerImServiceListener(eventName, handlers[eventName]);
      });
    },

    /**
     * 批量注册 EventBus 监听器
     * @param {Object} handlers - { eventName: handler, ... }
     */
    registerEventBusListeners(handlers) {
      if (!handlers || typeof handlers !== "object") {
        console.warn(
          `[eventListenerManager] registerEventBusListeners 参数必须是对象`
        );
        return;
      }

      Object.keys(handlers).forEach((eventName) => {
        this.registerEventBusListener(eventName, handlers[eventName]);
      });
    },

    /**
     * 批量注册 whiteBoardService 监听器
     * @param {Object} handlers - { eventName: handler, ... }
     */
    registerWhiteBoardServiceListeners(handlers) {
      if (!handlers || typeof handlers !== "object") {
        console.warn(
          `[eventListenerManager] registerWhiteBoardServiceListeners 参数必须是对象`
        );
        return;
      }

      Object.keys(handlers).forEach((eventName) => {
        this.registerWhiteBoardServiceListener(eventName, handlers[eventName]);
      });
    },

    /**
     * 批量注册 ipcRenderer 监听器（仅 Electron 环境）
     * @param {Object} handlers - { eventName: handler, ... }
     */
    registerIpcRendererListeners(handlers) {
      if (!handlers || typeof handlers !== "object") {
        console.warn(
          `[eventListenerManager] registerIpcRendererListeners 参数必须是对象`
        );
        return;
      }

      Object.keys(handlers).forEach((eventName) => {
        this.registerIpcRendererListener(eventName, handlers[eventName]);
      });
    },

    /**
     * 清理指定的 liveService 监听器
     * @param {string} eventName - 事件名称
     * @param {string} key - 监听器的唯一 key（可选），如果不传则清理该事件的所有监听器。如果 eventName 也不传，则清理所有
     */
    clearLiveServiceListener(eventName, key = null) {
      // 确保数据已初始化
      if (!this._liveServiceHandlers) {
        this._liveServiceHandlers = {};
        return;
      }

      if (!eventName) {
        // 如果 eventName 也不传，清理所有
        this.clearLiveServiceListeners();
        return;
      }

      if (!this._liveServiceHandlers[eventName]) {
        return;
      }

      if (key) {
        // 清理指定 key 的监听器
        const itemIndex = this._liveServiceHandlers[eventName].findIndex(
          (item) => item.key === key
        );
        if (itemIndex !== -1) {
          const item = this._liveServiceHandlers[eventName][itemIndex];
          if (this.liveService) {
            this.liveService.off(eventName, item.handler);
          }
          this._liveServiceHandlers[eventName].splice(itemIndex, 1);
          // 如果数组为空，删除该事件
          if (this._liveServiceHandlers[eventName].length === 0) {
            delete this._liveServiceHandlers[eventName];
          }
        }
      } else {
        // 清理该事件的所有监听器
        if (this.liveService) {
          this._liveServiceHandlers[eventName].forEach((item) => {
            this.liveService.off(eventName, item.handler);
          });
        }
        delete this._liveServiceHandlers[eventName];
      }
    },

    /**
     * 清理所有 liveService 监听器
     */
    clearLiveServiceListeners() {
      // 确保数据已初始化
      if (!this._liveServiceHandlers) {
        this._liveServiceHandlers = {};
        return;
      }

      if (this.liveService) {
        Object.keys(this._liveServiceHandlers).forEach((eventName) => {
          this._liveServiceHandlers[eventName].forEach((item) => {
            this.liveService.off(eventName, item.handler);
          });
        });
      }
      this._liveServiceHandlers = {};
    },

    /**
     * 清理指定的 imService 监听器
     * @param {string} eventName - 事件名称
     * @param {string} key - 监听器的唯一 key（可选），如果不传则清理该事件的所有监听器。如果 eventName 也不传，则清理所有
     */
    clearImServiceListener(eventName, key = null) {
      // 确保数据已初始化
      if (!this._imServiceHandlers) {
        this._imServiceHandlers = {};
        return;
      }

      if (!eventName) {
        // 如果 eventName 也不传，清理所有
        this.clearImServiceListeners();
        return;
      }

      if (!this._imServiceHandlers[eventName]) {
        return;
      }

      if (key) {
        // 清理指定 key 的监听器
        const itemIndex = this._imServiceHandlers[eventName].findIndex(
          (item) => item.key === key
        );
        if (itemIndex !== -1) {
          const item = this._imServiceHandlers[eventName][itemIndex];
          if (this.imService) {
            this.imService.off(eventName, item.handler);
          }
          this._imServiceHandlers[eventName].splice(itemIndex, 1);
          // 如果数组为空，删除该事件
          if (this._imServiceHandlers[eventName].length === 0) {
            delete this._imServiceHandlers[eventName];
          }
        }
      } else {
        // 清理该事件的所有监听器
        if (this.imService) {
          this._imServiceHandlers[eventName].forEach((item) => {
            this.imService.off(eventName, item.handler);
          });
        }
        delete this._imServiceHandlers[eventName];
      }
    },

    /**
     * 清理所有 imService 监听器
     */
    clearImServiceListeners() {
      //  确保数据已初始化
      if (!this._imServiceHandlers) {
        this._imServiceHandlers = {};
        return;
      }

      if (this.imService) {
        Object.keys(this._imServiceHandlers).forEach((eventName) => {
          this._imServiceHandlers[eventName].forEach((item) => {
            this.imService.off(eventName, item.handler);
          });
        });
      }
      this._imServiceHandlers = {};
    },

    /**
     * 清理指定的 EventBus 监听器
     * @param {string} eventName - 事件名称
     * @param {string} key - 监听器的唯一 key（可选），如果不传则清理该事件的所有监听器。如果 eventName 也不传，则清理所有
     */
    clearEventBusListener(eventName, key = null) {
      // 确保数据已初始化
      if (!this._eventBusHandlers) {
        this._eventBusHandlers = {};
        return;
      }

      if (!eventName) {
        // 如果 eventName 也不传，清理所有
        this.clearEventBusListeners();
        return;
      }

      if (!this._eventBusHandlers[eventName]) {
        return;
      }

      if (key) {
        // 清理指定 key 的监听器
        const itemIndex = this._eventBusHandlers[eventName].findIndex(
          (item) => item.key === key
        );
        if (itemIndex !== -1) {
          const item = this._eventBusHandlers[eventName][itemIndex];
          EventBus.$off(eventName, item.handler);
          this._eventBusHandlers[eventName].splice(itemIndex, 1);
          // 如果数组为空，删除该事件
          if (this._eventBusHandlers[eventName].length === 0) {
            delete this._eventBusHandlers[eventName];
          }
        }
      } else {
        // 清理该事件的所有监听器
        this._eventBusHandlers[eventName].forEach((item) => {
          EventBus.$off(eventName, item.handler);
        });
        delete this._eventBusHandlers[eventName];
      }
    },

    /**
     * 清理所有 EventBus 监听器
     */
    clearEventBusListeners() {
      if (!this._eventBusHandlers) {
        this._eventBusHandlers = {};
        return;
      }

      Object.keys(this._eventBusHandlers).forEach((eventName) => {
        this._eventBusHandlers[eventName].forEach((item) => {
          EventBus.$off(eventName, item.handler);
        });
      });
      this._eventBusHandlers = {};
    },

    /**
     * 清理指定的 whiteBoardService 监听器
     * @param {string} eventName - 事件名称
     * @param {string} key - 监听器的唯一 key（可选），如果不传则清理该事件的所有监听器。如果 eventName 也不传，则清理所有
     */
    clearWhiteBoardServiceListener(eventName, key = null) {
      // 确保数据已初始化
      if (!this._whiteBoardServiceHandlers) {
        this._whiteBoardServiceHandlers = {};
        return;
      }

      if (!eventName) {
        // 如果 eventName 也不传，清理所有
        this.clearWhiteBoardServiceListeners();
        return;
      }

      if (!this._whiteBoardServiceHandlers[eventName]) {
        return;
      }

      if (key) {
        // 清理指定 key 的监听器
        const itemIndex = this._whiteBoardServiceHandlers[eventName].findIndex(
          (item) => item.key === key
        );
        if (itemIndex !== -1) {
          const item = this._whiteBoardServiceHandlers[eventName][itemIndex];
          if (this.whiteBoardService) {
            this.whiteBoardService.off(eventName, item.handler);
          }
          this._whiteBoardServiceHandlers[eventName].splice(itemIndex, 1);
          // 如果数组为空，删除该事件
          if (this._whiteBoardServiceHandlers[eventName].length === 0) {
            delete this._whiteBoardServiceHandlers[eventName];
          }
        }
      } else {
        // 清理该事件的所有监听器
        if (this.whiteBoardService) {
          this._whiteBoardServiceHandlers[eventName].forEach((item) => {
            this.whiteBoardService.off(eventName, item.handler);
          });
        }
        delete this._whiteBoardServiceHandlers[eventName];
      }
    },

    /**
     * 清理所有 whiteBoardService 监听器
     */
    clearWhiteBoardServiceListeners() {
      // 确保数据已初始化
      if (!this._whiteBoardServiceHandlers) {
        this._whiteBoardServiceHandlers = {};
        return;
      }

      if (this.whiteBoardService) {
        Object.keys(this._whiteBoardServiceHandlers).forEach((eventName) => {
          this._whiteBoardServiceHandlers[eventName].forEach((item) => {
            this.whiteBoardService.off(eventName, item.handler);
          });
        });
      }
      this._whiteBoardServiceHandlers = {};
    },

    /**
     * 清理指定的 ipcRenderer 监听器
     * @param {string} eventName - 事件名称
     * @param {string} key - 监听器的唯一 key（可选），如果不传则清理该事件的所有监听器。如果 eventName 也不传，则清理所有
     */
    clearIpcRendererListener(eventName, key = null) {
      // 确保数据已初始化
      if (!this._ipcRendererHandlers) {
        this._ipcRendererHandlers = {};
        return;
      }

      if (!eventName) {
        // 如果 eventName 也不传，清理所有
        this.clearIpcRendererListeners();
        return;
      }

      if (!this._ipcRendererHandlers[eventName]) {
        return;
      }

      if (key) {
        // 清理指定 key 的监听器
        const itemIndex = this._ipcRendererHandlers[eventName].findIndex(
          (item) => item.key === key
        );
        if (itemIndex !== -1) {
          const item = this._ipcRendererHandlers[eventName][itemIndex];
          if (window.ipcRenderer) {
            window.ipcRenderer.removeListener(eventName, item.handler);
          }
          this._ipcRendererHandlers[eventName].splice(itemIndex, 1);
          // 如果数组为空，删除该事件
          if (this._ipcRendererHandlers[eventName].length === 0) {
            delete this._ipcRendererHandlers[eventName];
          }
        }
      } else {
        // 清理该事件的所有监听器
        if (window.ipcRenderer) {
          this._ipcRendererHandlers[eventName].forEach((item) => {
            window.ipcRenderer.removeListener(eventName, item.handler);
          });
        }
        delete this._ipcRendererHandlers[eventName];
      }
    },

    /**
     * 清理所有 ipcRenderer 监听器
     */
    clearIpcRendererListeners() {
      // 确保数据已初始化
      if (!this._ipcRendererHandlers) {
        this._ipcRendererHandlers = {};
        return;
      }

      if (window.ipcRenderer) {
        Object.keys(this._ipcRendererHandlers).forEach((eventName) => {
          this._ipcRendererHandlers[eventName].forEach((item) => {
            window.ipcRenderer.removeListener(eventName, item.handler);
          });
        });
      }
      this._ipcRendererHandlers = {};
    },

    /**
     * 安全的 setTimeout（自动管理，组件销毁时自动清理）
     * @param {Function} callback - 回调函数
     * @param {number} delay - 延迟时间（毫秒）
     * @param {string} key - 定时器 key（可选，用于后续清理或覆盖）。如果不提供，会自动生成唯一 key
     * @returns {string} 返回定时器 key
     */
    setTimeoutSafe(callback, delay, key) {
      if (!this._timers) {
        this._timers = new Map();
      }
      if (this._timerKeyCounter === undefined) {
        this._timerKeyCounter = 0;
      }

      if (typeof callback !== "function") {
        console.warn(
          `[eventListenerManager] setTimeoutSafe: callback 必须是函数`
        );
        return null;
      }

      // 如果没有提供 key，自动生成
      if (!key) {
        key = `_timeout_${Date.now()}_${++this._timerKeyCounter}`;
      }

      // 如果已存在同名定时器，先清理
      if (this._timers.has(key)) {
        const existing = this._timers.get(key);
        if (existing.type === "timeout") {
          clearTimeout(existing.id);
        } else {
          clearInterval(existing.id);
        }
      }

      // 创建新的定时器
      const timerId = setTimeout(() => {
        // 执行回调
        callback();
        // 执行完后从 Map 中移除
        this._timers.delete(key);
      }, delay);

      // 保存到 Map
      this._timers.set(key, { type: "timeout", id: timerId });

      return key;
    },

    /**
     * 安全的 setInterval（自动管理，组件销毁时自动清理）
     * @param {Function} callback - 回调函数
     * @param {number} delay - 间隔时间（毫秒）
     * @param {string} key - 定时器 key（可选，用于后续清理或覆盖）。如果不提供，会自动生成唯一 key
     * @returns {string} 返回定时器 key
     */
    setIntervalSafe(callback, delay, key) {
      if (!this._timers) {
        this._timers = new Map();
      }
      if (this._timerKeyCounter === undefined) {
        this._timerKeyCounter = 0;
      }

      if (typeof callback !== "function") {
        console.warn(
          `[eventListenerManager] setIntervalSafe: callback 必须是函数`
        );
        return null;
      }

      // 如果没有提供 key，自动生成
      if (!key) {
        key = `_interval_${Date.now()}_${++this._timerKeyCounter}`;
      }

      // 如果已存在同名定时器，先清理
      if (this._timers.has(key)) {
        const existing = this._timers.get(key);
        if (existing.type === "timeout") {
          clearTimeout(existing.id);
        } else {
          clearInterval(existing.id);
        }
      }

      // 创建新的定时器
      const timerId = setInterval(callback, delay);

      // 保存到 Map
      this._timers.set(key, { type: "interval", id: timerId });

      return key;
    },

    /**
     * 清理指定的定时器
     * @param {string} key - 定时器 key
     */
    clearTimer(key) {
      if (!key) {
        console.warn(`[eventListenerManager] clearTimer: key 不能为空`);
        return;
      }

      if (!this._timers) {
        return;
      }

      const timer = this._timers.get(key);
      if (timer) {
        if (timer.type === "timeout") {
          clearTimeout(timer.id);
        } else {
          clearInterval(timer.id);
        }
        this._timers.delete(key);
      }
    },

    /**
     * 清理所有定时器
     */
    clearAllTimers() {
      if (!this._timers) {
        return;
      }

      this._timers.forEach((timer, key) => {
        if (timer.type === "timeout") {
          clearTimeout(timer.id);
        } else {
          clearInterval(timer.id);
        }
      });
      this._timers.clear();
    },

    /**
     * 获取所有定时器的 key 列表（用于调试）
     * @returns {Array<string>}
     */
    getTimerKeys() {
      if (!this._timers) {
        return [];
      }
      return Array.from(this._timers.keys());
    },
  },
};
